'use client';

import React from 'react';

interface ScrollToFormLinkProps {
  /** Element id to scroll into view. Defaults to the form card. */
  targetId?: string;
  /** Element id to flash with the yellow highlight. Defaults to `targetId`. */
  highlightTargetId?: string;
  className?: string;
  children: React.ReactNode;
}

const HIGHLIGHT_COLOR = '#FFC907';
const HIGHLIGHT_DURATION_MS = 2400;
// 4px solid ring + an outer glow so the focus state is visible on mobile
// even against the navy background where a thin ring is easy to miss.
const HIGHLIGHT_SHADOW = `0 0 0 4px ${HIGHLIGHT_COLOR}, 0 0 24px 4px ${HIGHLIGHT_COLOR}80`;

/**
 * Renders an <a> that smooth-scrolls to a target id without pushing the
 * hash (`#referral-form-card`) onto the URL. Also briefly draws a yellow
 * ring around the form card so the user's eye lands on it.
 */
export default function ScrollToFormLink({
  targetId = 'referral-form-card',
  highlightTargetId,
  className,
  children,
}: ScrollToFormLinkProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof document === 'undefined') return;
    const scrollEl = document.getElementById(targetId);
    const highlightEl = document.getElementById(highlightTargetId ?? targetId);

    if (scrollEl) {
      e.preventDefault();
      scrollEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (highlightEl) {
      const el = highlightEl as HTMLElement & { __highlightTimer?: number };
      // Defer one frame so the scroll commits first, then paint the ring.
      // Use setProperty(..., 'important') to beat the form's Tailwind
      // `shadow-[...]` class without specificity fights.
      window.requestAnimationFrame(() => {
        el.style.setProperty('transition', 'box-shadow 0.3s ease', 'important');
        el.style.setProperty('box-shadow', HIGHLIGHT_SHADOW, 'important');
      });
      if (el.__highlightTimer) window.clearTimeout(el.__highlightTimer);
      el.__highlightTimer = window.setTimeout(() => {
        el.style.removeProperty('box-shadow');
        el.style.removeProperty('transition');
      }, HIGHLIGHT_DURATION_MS);
    }
  };

  return (
    <a href={`#${targetId}`} onClick={handleClick} className={className}>
      {children}
    </a>
  );
}
